/**
 * MarketingAudienceDetailPage — view one dynamic segment: rules, counts,
 * refresh, and paginated materialized members from segment_memberships.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import PillarPageShell from "../shared/PillarPageShell";
import { ActionButton } from "../../ops";
import apiService from "../../../services/api";
import MemberLocation, { MemberLocationFields } from '../../common/MemberLocation';

const LIST_PATH = "/dashboard/communications/marketing/audiences";
const PAGE_SIZE = 25;

interface SegmentDetail {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  is_system: boolean;
  is_active: boolean;
  filter_json: unknown;
  last_count: number | null;
  last_refreshed_at: string | null;
  refresh_error?: string | null;
}

interface MemberRow extends MemberLocationFields {
  id: string;
  email: string | null;
  name: string | null;
  subscription_plan: string | null;
  subscription_status: string | null;
  added_at: string | null;
}

/**
 * `toLocaleString()` does NOT throw on an unparseable date — it returns the
 * literal string "Invalid Date". The old try/catch therefore caught nothing and
 * a bad timestamp rendered as "Invalid Date" in the Last-materialized card and
 * in every Added cell. Test the time value instead (same rule as
 * emailOverviewShared.fmtWhen).
 */
function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

const MarketingAudienceDetailPage: React.FC = () => {
  const { segmentId } = useParams<{ segmentId: string }>();
  const navigate = useNavigate();
  const [segment, setSegment] = useState<SegmentDetail | null>(null);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [memberTotal, setMemberTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshBusy, setRefreshBusy] = useState(false);

  const loadSegment = useCallback(async (): Promise<SegmentDetail | null> => {
    if (!segmentId) return null;
    const res = await apiService.get<SegmentDetail>(
      `/api/admin/segments/${encodeURIComponent(segmentId)}`,
    );
    const seg = res.data as SegmentDetail;
    setSegment(seg);
    return seg;
  }, [segmentId]);

  // Monotonic request id for the member list. The effect's `cancelled` flag
  // could never protect these writes — it guarded the awaiting effect body, not
  // the setMembers/setMemberTotal calls inside loadMembers, so page 1 landing
  // after page 2 overwrote page 2's rows (and its total). A superseded response
  // now writes nothing at all.
  const membersSeq = useRef(0);

  const loadMembers = useCallback(async () => {
    if (!segmentId) return;
    const seq = ++membersSeq.current;
    const stale = () => membersSeq.current !== seq;
    setMembersLoading(true);
    try {
      const offset = page * PAGE_SIZE;
      const res = await apiService.get<MemberRow[]>(
        `/api/admin/segments/${encodeURIComponent(segmentId)}/members?limit=${PAGE_SIZE}&offset=${offset}`,
      );
      if (stale()) return;
      setMembers(Array.isArray(res.data) ? res.data : []);
      setMemberTotal(typeof res.total === "number" ? res.total : 0);
    } finally {
      if (!stale()) setMembersLoading(false);
    }
  }, [segmentId, page]);

  useEffect(() => {
    setPage(0);
  }, [segmentId]);

  useEffect(() => {
    if (!segmentId) {
      setError("Missing audience id");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSegment(null);
    (async () => {
      try {
        const seg = await loadSegment();
        if (!cancelled && seg && !seg.last_refreshed_at) {
          await apiService.post(
            `/api/admin/segments/${encodeURIComponent(segmentId)}/refresh`,
          );
          if (!cancelled) await loadSegment();
        }
      } catch (e: unknown) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Failed to load audience");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [segmentId, loadSegment]);

  useEffect(() => {
    if (!segmentId || loading || !segment) return;
    let cancelled = false;
    (async () => {
      try {
        await loadMembers();
      } catch (e: unknown) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Failed to load members");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [segmentId, page, loading, loadMembers, segment]);

  const handleRefreshSegment = async () => {
    if (!segmentId) return;
    setRefreshBusy(true);
    setError(null);
    try {
      await apiService.post(`/api/admin/segments/${encodeURIComponent(segmentId)}/refresh`);
      await loadSegment();
      await loadMembers();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Refresh failed");
    } finally {
      setRefreshBusy(false);
    }
  };

  if (!segmentId) {
    return (
      <PillarPageShell pillar="marketing" title="Audience">
        <Alert severity="error">Invalid link.</Alert>
      </PillarPageShell>
    );
  }

  return (
    <PillarPageShell
      pillar="marketing"
      title={segment?.name ?? "Audience"}
      subtitle={
        segment
          ? `${segment.slug} · ${segment.is_system ? "System" : "Custom"} audience`
          : "Loading…"
      }
      rightActions={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(LIST_PATH)}
            size="small"
            variant="outlined"
          >
            Back to list
          </Button>
          <ActionButton
            verb="refresh"
            labelOverride="Refresh audience"
            size="small"
            showIcon={false}
            loading={refreshBusy}
            onClick={() => void handleRefreshSegment()}
          />
        </Box>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !segment ? (
        error ? null : (
          <Alert severity="warning">Audience not found.</Alert>
        )
      ) : (
        <>
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Last materialized
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {formatWhen(segment.last_refreshed_at)}
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  · {segment.last_count?.toLocaleString() ?? "—"} members (stored count)
                </Typography>
              </Typography>
              {segment.refresh_error && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {segment.refresh_error}
                </Alert>
              )}
              <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <RefreshIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  The Refresh audience action re-runs the filter and updates memberships. Large
                  audiences may take a moment.
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, mb: 1, color: "#1A5F7A" }}
              >
                Filter rules (filter_json)
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                  fontSize: "0.75rem",
                  overflow: "auto",
                  maxHeight: 280,
                }}
              >
                {JSON.stringify(segment.filter_json ?? {}, null, 2)}
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, mb: 1, color: "#1A5F7A" }}
              >
                Members in this audience
              </Typography>
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Added</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {membersLoading && members.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : members.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary">
                            No materialized members yet. Use Refresh audience to populate this list from
                            current users matching the rules.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      members.map((m) => (
                        <TableRow key={`${m.id}-${m.added_at ?? ""}`} hover>
                          <TableCell>{m.email ?? "—"}</TableCell>
                          <TableCell>{m.name ?? "—"}</TableCell>
                          <TableCell><MemberLocation
                        location_country_code={m.location_country_code}
                        location_region={m.location_region}
                        location_city={m.location_city}
                        location_source={m.location_source}
                        location_confidence={m.location_confidence}
                        location_resolved_at={m.location_resolved_at}
                        compact
                      /></TableCell>
                          <TableCell>{m.subscription_plan ?? "—"}</TableCell>
                          <TableCell>{m.subscription_status ?? "—"}</TableCell>
                          <TableCell>{formatWhen(m.added_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={memberTotal}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                onRowsPerPageChange={() => {}}
              />
            </CardContent>
          </Card>
        </>
      )}
    </PillarPageShell>
  );
};

export default MarketingAudienceDetailPage;
